/**
 * DualCTA page-exit reason — single source of truth for PostHog + Slack.
 *
 * Hotfix 2026-07-29 (CO Instagram Android session):
 * Short teasers (~11s) with real watch (e.g. 5s / 47%) were labeled
 * `no_video_no_click` because partial watch required >10s absolute time.
 * Slack then titled that as "video no arrancó" despite video_started=true.
 *
 * Rule: any play signal (started / playhead / wall-clock watch) without end
 * is partial engagement — never "no video".
 */

export type DualCtaExitReason =
  | "video_error"
  | "video_never_played"
  | "post_video_quick_exit"
  | "watched_full_no_click"
  | "partial_watch_no_click"
  | "immediate_bounce"
  | "no_video_no_click";

export type DualCtaExitReasonInput = {
  videoBroken: boolean;
  videoStarted: boolean;
  videoEnded: boolean;
  /** Seconds actually watched (Mux playhead preferred, wall-clock fallback). */
  videoWatchedSec: number;
  /** Media duration when known; null if Mux never reported it. */
  videoDurationSec: number | null;
  secOnPage: number;
};

/**
 * Milestone % of duration (0–100). Used for short-teaser partial detection.
 */
export function dualCtaWatchMilestonePct(
  watchedSec: number,
  durationSec: number | null,
  ended: boolean,
): number {
  if (ended) return 100;
  if (durationSec != null && durationSec > 1) {
    return Math.min(100, Math.max(0, Math.round((watchedSec / durationSec) * 100)));
  }
  // Legacy heuristic when duration unknown (long teasers 30–60s).
  if (watchedSec >= 30) return 75;
  if (watchedSec >= 15) return 50;
  if (watchedSec >= 5) return 25;
  return watchedSec > 0 ? 10 : 0;
}

/**
 * True when the visitor got a real look at the teaser (not poster-only).
 * Duration-aware: 2s on an 11s asset (~18%) counts; fixed >10s does not.
 */
export function dualCtaHadMeaningfulWatch(input: {
  videoStarted: boolean;
  videoWatchedSec: number;
  videoDurationSec: number | null;
  videoEnded: boolean;
}): boolean {
  const { videoStarted, videoWatchedSec, videoDurationSec, videoEnded } = input;
  if (videoEnded) return true;
  if (videoStarted) return true;
  if (videoWatchedSec > 0) return true;
  const milestone = dualCtaWatchMilestonePct(
    videoWatchedSec,
    videoDurationSec,
    false,
  );
  return milestone >= 15;
}

export function resolveDualCtaExitReason(
  input: DualCtaExitReasonInput,
): DualCtaExitReason {
  const {
    videoBroken,
    videoStarted,
    videoEnded,
    videoWatchedSec,
    videoDurationSec,
    secOnPage,
  } = input;

  const neverSawVideo =
    !videoStarted && videoWatchedSec <= 0 && !videoEnded;

  if (videoBroken) return "video_error";

  // Soft-stall / poster-only long enough to matter for ops.
  if (neverSawVideo && secOnPage >= 4) return "video_never_played";

  if (videoEnded && secOnPage < 5) return "post_video_quick_exit";
  if (videoEnded) return "watched_full_no_click";

  // Any real play without end → partial engagement (short teasers included).
  if (
    dualCtaHadMeaningfulWatch({
      videoStarted,
      videoWatchedSec,
      videoDurationSec,
      videoEnded: false,
    })
  ) {
    return "partial_watch_no_click";
  }

  if (secOnPage < 4) return "immediate_bounce";
  if (neverSawVideo) return "video_never_played";

  // Safety net: CTAs shown, no play signal, ≥4s — still convertible DualCTA.
  return "no_video_no_click";
}
